/**
 * MinorConsentInitiatePage
 * Route: /consent/minor/initiate
 *
 * A minor user (under 18) initiates the parental consent flow.
 * They enter their parent's contact info to receive a verification link.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield,
  Mail,
  Smartphone,
  ArrowLeft,
  Loader2,
  CheckCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { toast } from "sonner";
import { initiateMinorConsent } from "@/services/dpdpApi";

export default function MinorConsentInitiatePage() {
  const navigate = useNavigate();
  const [contactMethod, setContactMethod] = useState("email");
  const [contactValue, setContactValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [verificationId, setVerificationId] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contactValue.trim()) {
      setError("Please enter your parent's contact information.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const result = await initiateMinorConsent({
        parent_contact_method: contactMethod,
        parent_contact_value: contactValue.trim(),
      });
      setVerificationId(result.verification_id || "");
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Failed to initiate parental consent.");
      toast.error(err.message || "Failed to initiate.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-slate-900 dark:via-indigo-950 dark:to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardHeader>
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <CardTitle className="text-xl">Request Sent</CardTitle>
            <CardDescription>
              A verification link has been sent to your parent via{" "}
              {contactMethod}. Ask them to check their{" "}
              {contactMethod === "email" ? "email inbox" : "phone messages"} and
              click the link to verify. The link is valid for 7 days.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {verificationId && (
              <div className="rounded-lg bg-muted/50 p-3 text-xs">
                <strong>Your verification ID:</strong>{" "}
                <span className="font-mono">{verificationId.slice(0, 8)}...</span>
                <p className="text-muted-foreground mt-1">
                  Save this to check your consent status later.
                </p>
              </div>
            )}
            <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-4 text-sm text-left">
              <strong>What happens next?</strong>
              <ol className="list-decimal list-inside mt-2 space-y-1 text-muted-foreground">
                <li>Your parent receives the verification link</li>
                <li>They verify their identity (existing account or DigiLocker)</li>
                <li>You will be able to use the platform normally</li>
              </ol>
            </div>
            <Button
              className="w-full"
              onClick={() => navigate("/")}
            >
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-slate-900 dark:via-indigo-950 dark:to-slate-900 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-indigo-600 dark:text-indigo-300" />
          </div>
          <CardTitle className="text-2xl text-center">
            Parental Consent Required
          </CardTitle>
          <CardDescription className="text-center">
            Since you are under 18, your parent or guardian needs to verify
            consent before you can use the platform.
            <br />
            <span className="text-xs text-muted-foreground">
              DPDP Act 2023, Section 10
            </span>
          </CardDescription>
        </CardHeader>

        <CardContent>
          <Button
            variant="ghost"
            size="sm"
            className="mb-4"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="mb-2 block">
                How should we contact your parent?
              </Label>
              <RadioGroup
                value={contactMethod}
                onValueChange={setContactMethod}
                className="space-y-2"
              >
                <div className="flex items-center space-x-2 rounded-lg border p-3 cursor-pointer hover:bg-muted/50">
                  <RadioGroupItem value="email" id="method-email" />
                  <Label
                    htmlFor="method-email"
                    className="flex items-center gap-2 cursor-pointer flex-1"
                  >
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    Email address
                  </Label>
                </div>
                <div className="flex items-center space-x-2 rounded-lg border p-3 cursor-pointer hover:bg-muted/50">
                  <RadioGroupItem value="phone" id="method-phone" />
                  <Label
                    htmlFor="method-phone"
                    className="flex items-center gap-2 cursor-pointer flex-1"
                  >
                    <Smartphone className="h-4 w-4 text-muted-foreground" />
                    Phone number
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div>
              <Label htmlFor="contact-value">
                Parent&apos;s{" "}
                {contactMethod === "email" ? "email address" : "phone number"}
              </Label>
              <Input
                id="contact-value"
                type={contactMethod === "email" ? "email" : "tel"}
                placeholder={
                  contactMethod === "email"
                    ? "parent@example.com"
                    : "+91 98765 43210"
                }
                value={contactValue}
                onChange={(e) => setContactValue(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Your parent&apos;s contact will be hashed and only used for this
                verification.
              </p>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 p-3 text-sm text-red-600">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={loading || !contactValue.trim()}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending request...
                </>
              ) : (
                "Send Verification Link to Parent"
              )}
            </Button>
          </form>

          <div className="mt-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 text-xs text-muted-foreground">
            <strong>Privacy note:</strong> Your parent&apos;s contact information
            is hashed with SHA-256 before storage. It is never stored in
            plaintext and is deleted after verification is complete.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
